import React, { useContext, useEffect, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import axios from "axios";

import { AuthContext } from "../contexts/AuthContext";
import { ClientContext } from "../contexts/ClientContext";

import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Collapse,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  Typography,
  Avatar,
  Divider,
} from "@material-ui/core";
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Room as RoomIcon,
  Business as BusinessIcon,
  ExpandMore as ExpandMoreIcon,
  Person as PersonIcon,
  Schedule as ScheduleIcon,
  AccessTime as AccessTimeIcon,
} from "@material-ui/icons";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8080/";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(2),
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(1),
    },
  },
  searchBar: {
    marginBottom: theme.spacing(3),
    "& .MuiOutlinedInput-root": {
      backgroundColor: theme.palette.background.paper,
    },
  },
  clientCard: {
    height: "100%",
    display: "flex",
    flexDirection: "column",
    transition: "transform 0.2s, box-shadow 0.2s",
    "&:hover": {
      transform: "translateY(-4px)",
      boxShadow: theme.shadows[8],
    },
  },
  cardHeader: {
    paddingBottom: theme.spacing(1),
    alignItems: "flex-start",
    "& .MuiCardHeader-title": {
      fontSize: "1.1rem",
      fontWeight: 600,
      color: theme.palette.primary.main,
    },
  },
  cardContent: {
    paddingTop: theme.spacing(1),
    "&:last-child": {
      paddingBottom: theme.spacing(2),
    },
  },
  infoRow: {
    display: "flex",
    alignItems: "center",
    marginBottom: theme.spacing(1),
    gap: theme.spacing(1),
  },
  icon: {
    fontSize: "1.2rem",
    color: theme.palette.text.secondary,
  },
  link: {
    color: theme.palette.primary.main,
    textDecoration: "none",
    cursor: "pointer",
    "&:hover": {
      textDecoration: "underline",
    },
  },
  typeBadge: {
    height: 20,
    fontSize: "0.65rem",
    [theme.breakpoints.up("sm")]: {
      fontSize: "0.7rem",
      height: 22,
    },
  },
  idTypeRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginTop: theme.spacing(0.5),
  },
  idChip: {
    height: 20,
    fontSize: "0.65rem",
    backgroundColor: theme.palette.grey[300],
    color: theme.palette.text.secondary,
    [theme.breakpoints.up("sm")]: {
      fontSize: "0.7rem",
      height: 22,
    },
  },
  statsContainer: {
    display: "flex",
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
    flexWrap: "wrap",
  },
  statChip: {
    fontSize: "0.75rem",
  },
  expandButton: {
    transform: "rotate(0deg)",
    transition: theme.transitions.create("transform", {
      duration: theme.transitions.duration.shortest,
    }),
  },
  expandButtonOpen: {
    transform: "rotate(180deg)",
  },
  contactsContainer: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.grey[50],
    borderRadius: theme.shape.borderRadius,
  },
  contactCard: {
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(1),
    backgroundColor: theme.palette.background.paper,
    borderLeft: `3px solid ${theme.palette.primary.main}`,
    "&:last-child": {
      marginBottom: 0,
    },
  },
  noResults: {
    textAlign: "center",
    padding: theme.spacing(6),
    color: theme.palette.text.secondary,
  },
}));

const Clients = () => {
  const classes = useStyles();
  const { token } = useContext(AuthContext);
  const { loadClients, clients } = useContext(ClientContext);

  const [searchFilter, setSearchFilter] = useState("");
  const [expandedCards, setExpandedCards] = useState(new Set());
  const [cardContacts, setCardContacts] = useState({});

  useEffect(() => {
    loadClients();
  }, []);

  const handleSearchChange = (event) => {
    setSearchFilter(event.target.value);
  };

  const handleClearSearch = () => {
    setSearchFilter("");
  };

  const handleToggleExpand = async (clientId) => {
    const newExpanded = new Set(expandedCards);
    
    if (newExpanded.has(clientId)) {
      newExpanded.delete(clientId);
      setExpandedCards(newExpanded);
    } else {
      newExpanded.add(clientId);
      // Expand immediately for instant feedback
      setExpandedCards(newExpanded);
      
      // Load contacts in background if not already cached
      if (!cardContacts[clientId]) {
        try {
          const response = await axios.get(
            `${API_URL}get-client-contacts/${clientId}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          setCardContacts(prev => ({
            ...prev,
            [clientId]: response.data.contacts || []
          }));
        } catch (error) {
          console.error("Failed to load contacts:", error);
          setCardContacts(prev => ({
            ...prev,
            [clientId]: []
          }));
        }
      }
    }
  };

  const filteredClients = clients.filter((client) => {
    if (!searchFilter) return true;

    const searchLower = searchFilter.toLowerCase();
    
    const mainFieldsMatch = [
      client.Name,
      client.Type,
      client.Contact,
      client.Phone,
      client.Email,
      client.Address1,
      client.City,
      client.State,
    ]
      .filter(Boolean)
      .some((field) => field.toLowerCase().includes(searchLower));

    const contactsMatch =
      client.additional_contacts_data &&
      client.additional_contacts_data.toLowerCase().includes(searchLower);

    return mainFieldsMatch || contactsMatch;
  }).sort((a, b) => {
    // Sort by client name alphabetically (case-insensitive)
    return a.Name.toLowerCase().localeCompare(b.Name.toLowerCase());
  });

  const formatAddress = (client) => {
    const parts = [
      client.Address1,
      client.Address2,
      client.City,
      client.State,
      client.Zip,
    ].filter(Boolean);
    return parts.join(", ");
  };

  const getGoogleMapsUrl = (client) => {
    const address = formatAddress(client);
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
  };

  const formatPhone = (phone) => {
    if (!phone) return "";
    // Remove non-digits
    const digits = phone.replace(/\D/g, "");
    // Format as (XXX) XXX-XXXX if 10 digits
    if (digits.length === 10) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    }
    return phone;
  };

  return (
    <Box className={classes.root}>
      <Paper elevation={0} variant="outlined" style={{ padding: 16, marginBottom: 24 }}>
        <Typography variant="h5" gutterBottom>
          <BusinessIcon style={{ verticalAlign: "middle", marginRight: 8 }} />
          Client Directory
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Search by company name, contact, phone, email, or address
        </Typography>
      </Paper>

      <TextField
        fullWidth
        variant="outlined"
        placeholder="Search clients, contacts, phone numbers, emails..."
        value={searchFilter}
        onChange={handleSearchChange}
        className={classes.searchBar}
        id="client-search-input"
        name="clientSearch"
        autoComplete="off"
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
          endAdornment: searchFilter && (
            <InputAdornment position="end">
              <IconButton size="small" onClick={handleClearSearch} aria-label="Clear search">
                <ClearIcon />
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      {filteredClients.length === 0 ? (
        <Box className={classes.noResults}>
          <SearchIcon style={{ fontSize: 64, marginBottom: 16 }} />
          <Typography variant="h6" gutterBottom>
            No clients found
          </Typography>
          <Typography variant="body2">
            Try adjusting your search terms
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={2}>
          {filteredClients.map((client) => {
            const isExpanded = expandedCards.has(client.ID);
            const contacts = cardContacts[client.ID] || [];
            const hasAdditionalContacts = client.additional_contacts_count > 0;

            return (
              <Grid item xs={12} sm={6} md={4} key={client.ID}>
                <Card className={classes.clientCard} elevation={2}>
                  <CardHeader
                    className={classes.cardHeader}
                    avatar={
                      <Avatar style={{ backgroundColor: "#1976d2" }}>
                        {client.Name.charAt(0).toUpperCase()}
                      </Avatar>
                    }
                    title={
                      <>
                        {/* Company Name */}
                        <Box component="span">{client.Name}</Box>
                        
                        {/* ID and Type on same line (both mobile and desktop) */}
                        <Box className={classes.idTypeRow}>
                          <Chip
                            label={`ID: ${client.ID}`}
                            size="small"
                            className={classes.idChip}
                          />
                          {client.Type && (
                            <Chip
                              label={client.Type}
                              size="small"
                              className={classes.typeBadge}
                              color="primary"
                              variant="outlined"
                            />
                          )}
                        </Box>
                      </>
                    }
                  />

                  <CardContent className={classes.cardContent}>
                    {/* Primary Contact */}
                    {client.Contact && (
                      <Box className={classes.infoRow}>
                        <PersonIcon className={classes.icon} />
                        <Typography variant="body2">{client.Contact}</Typography>
                      </Box>
                    )}

                    {/* Phone */}
                    {client.Phone && (
                      <Box className={classes.infoRow}>
                        <PhoneIcon className={classes.icon} />
                        <a
                          href={`tel:${client.Phone}`}
                          className={classes.link}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Typography variant="body2">
                            {formatPhone(client.Phone)}
                          </Typography>
                        </a>
                      </Box>
                    )}

                    {/* Email - Only show if email exists */}
                    {client.Email && (
                      <Box className={classes.infoRow}>
                        <EmailIcon className={classes.icon} />
                        <a
                          href={`mailto:${client.Email}`}
                          className={classes.link}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Typography variant="body2" noWrap>
                            {client.Email}
                          </Typography>
                        </a>
                      </Box>
                    )}

                    {/* Address */}
                    {client.Address1 && (
                      <Box className={classes.infoRow}>
                        <RoomIcon className={classes.icon} />
                        <a
                          href={getGoogleMapsUrl(client)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={classes.link}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Typography variant="body2">
                            {formatAddress(client)}
                          </Typography>
                        </a>
                      </Box>
                    )}

                    {/* Stats */}
                    <Box className={classes.statsContainer}>
                      {client.membersince && (
                        <Chip
                          icon={<ScheduleIcon />}
                          label={`Since ${new Date(client.membersince).getFullYear()}`}
                          size="small"
                          className={classes.statChip}
                        />
                      )}
                    </Box>

                    {/* Additional Contacts Toggle */}
                    {hasAdditionalContacts && (
                      <>
                        <Divider style={{ margin: "12px 0 8px 0" }} />
                        <Box
                          display="flex"
                          alignItems="center"
                          justifyContent="space-between"
                          style={{ cursor: "pointer" }}
                          onClick={() => handleToggleExpand(client.ID)}
                        >
                          <Typography variant="body2" color="primary">
                            {isExpanded ? "Hide" : "Show"} {client.additional_contacts_count} additional contact
                            {client.additional_contacts_count > 1 ? "s" : ""}
                          </Typography>
                          <IconButton
                            size="small"
                            className={`${classes.expandButton} ${
                              isExpanded ? classes.expandButtonOpen : ""
                            }`}
                          >
                            <ExpandMoreIcon />
                          </IconButton>
                        </Box>

                        <Collapse in={isExpanded}>
                          <Box className={classes.contactsContainer}>
                            {contacts.length === 0 ? (
                              <Typography variant="body2" color="textSecondary">
                                Loading contacts...
                              </Typography>
                            ) : (
                              contacts.map((contact, idx) => (
                                <Box key={idx} className={classes.contactCard}>
                                  <Typography
                                    variant="subtitle2"
                                    style={{ fontWeight: 600, marginBottom: 4 }}
                                  >
                                    {contact.Contact}
                                  </Typography>
                                  {contact.MobilePhone && (
                                    <Box className={classes.infoRow} style={{ marginBottom: 4 }}>
                                      <PhoneIcon style={{ fontSize: "1rem" }} />
                                      <a
                                        href={`tel:${contact.MobilePhone}`}
                                        className={classes.link}
                                        onClick={(e) => e.stopPropagation()}
                                      >
                                        <Typography variant="body2">
                                          {formatPhone(contact.MobilePhone)}
                                        </Typography>
                                      </a>
                                    </Box>
                                  )}
                                  {contact.Email && (
                                    <Box className={classes.infoRow}>
                                      <EmailIcon style={{ fontSize: "1rem" }} />
                                      <a
                                        href={`mailto:${contact.Email}`}
                                        className={classes.link}
                                        onClick={(e) => e.stopPropagation()}
                                      >
                                        <Typography variant="body2" noWrap>
                                          {contact.Email}
                                        </Typography>
                                      </a>
                                    </Box>
                                  )}
                                </Box>
                              ))
                            )}
                          </Box>
                        </Collapse>
                      </>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}
    </Box>
  );
};

export default Clients;
